import { Box, Skeleton, Typography } from "@mui/material";
import { ThemeProperties } from "../Theme/ThemeProperties";

// Fixed pixel widths — used only for sticky columns (left offset calc) and table minWidth
const METRIC_COL_WIDTH   = 260;  // col index 0
const STICKY_COL_WIDTH   = 170;  // any other sticky col
const MIN_DATA_COL_WIDTH = 120;  // minimum width for non-sticky (flex) cols

function getColWidth(colIndex) {
    return colIndex === 0 ? METRIC_COL_WIDTH : STICKY_COL_WIDTH;
}

/**
 * Left offset for a sticky column = sum of widths of all preceding sticky columns.
 */
function getStickyLeft(colIndex, stickySet) {
    let left = 0;
    for (let i = 0; i < colIndex; i++) {
        if (stickySet.has(i)) left += getColWidth(i);
    }
    return left;
}

/**
 * StringWiseMetricTable
 *
 * Props:
 *   columns       : string[]   — ordered column keys; first entry is the metric/label column
 *   rows          : Array<{
 *                     values      : { [col]: string | number },
 *                     highlighted : { [col]: boolean }
 *                   }>
 *   stickyColumns : number[]   — column indices that stay fixed on horizontal scroll
 *                               (e.g. [0] keeps the metric column fixed)
 *   loading       : boolean    — show skeleton rows while data is loading
 *   highlightColor: string     — color for highlighted values (default #B50500)
 */
export default function StringWiseMetricTable({
    columns = [],
    rows = [],
    stickyColumns = [],
    loading = false,
    highlightColor = ThemeProperties.safety_1,
}) {
    const stickySet = new Set(stickyColumns);

    // minWidth = sum of sticky col widths + (non-sticky count × MIN_DATA_COL_WIDTH)
    const minWidth = columns.reduce((sum, _, i) => {
        return sum + (stickySet.has(i) ? getColWidth(i) : MIN_DATA_COL_WIDTH);
    }, 0);

    // Builds sx for a cell at the given column index.
    // Sticky cols: fixed width. Non-sticky cols: flex:1 to fill remaining space equally.
    function cellSx(colIndex, extra = {}) {
        const isSticky = stickySet.has(colIndex);
        return {
            py: 2,
            px: 2,
            ...(isSticky
                ? {
                      width: `${getColWidth(colIndex)}px`,
                      flex: "0 0 auto",
                      position: "sticky",
                      left: getStickyLeft(colIndex, stickySet),
                      zIndex: 2,
                  }
                : {
                      flex: 1,
                      minWidth: `${MIN_DATA_COL_WIDTH}px`,
                  }),
            ...extra,
        };
    }

    const headerBg = "transparent";
    const bodyBg   = "transparent";

    return (
        <Box sx={{ width: "98%", border: "1px solid #E6E6E6", borderRadius: "12px", overflow: "hidden", px: 2, py:2 }}>
            <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minWidth: `${minWidth}px` }}>

                    {/* ── Header row ── */}
                    <Box sx={{ display: "flex", backgroundColor: headerBg, borderBottom: "1px solid #F0F0F0" }}>
                        {columns.map((col, colIndex) => (
                            <Box key={col} sx={cellSx(colIndex, { backgroundColor: headerBg, py: 1.5 })}>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        lineHeight: "20px",
                                        color: "#000000",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    {col}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* ── Data rows ── */}
                    {loading
                        ? Array.from({ length: 5 }).map((_, rowIndex) => (
                              <Box key={rowIndex} sx={{ display: "flex" }}>
                                  {columns.map((col, colIndex) => (
                                      <Box
                                          key={col}
                                          sx={cellSx(colIndex, {
                                              backgroundColor: bodyBg,
                                              borderBottom: "1px solid #F0F0F0",
                                              display: "flex",
                                              alignItems: "center",
                                          })}
                                      >
                                          <Skeleton width={colIndex === 0 ? "60%" : "40%"} height={20} />
                                      </Box>
                                  ))}
                              </Box>
                          ))
                        : rows.map((row, rowIndex) => {
                              const isLastRow = rowIndex === rows.length - 1;
                              return (
                                  <Box key={rowIndex} sx={{ display: "flex" }}>
                                      {columns.map((col, colIndex) => {
                                          const val = row.values?.[col];
                                          const isHighlighted = row.highlighted?.[col] === true;
                                          const display = [null, undefined, ""].includes(val) ? "-" : val;
                                          const isMetricCol = colIndex === 0;

                                          return (
                                              <Box
                                                  key={col}
                                                  sx={cellSx(colIndex, {
                                                      backgroundColor: bodyBg,
                                                      borderBottom: isLastRow ? "none" : "1px solid #F0F0F0",
                                                      display: "flex",
                                                      alignItems: "center",
                                                  })}
                                              >
                                                  <Typography
                                                      sx={{
                                                          fontSize: "14px",
                                                          fontWeight: 400,
                                                          lineHeight: "20px",
                                                          textTransform: "capitalize",
                                                          color: isHighlighted && !isMetricCol
                                                              ? highlightColor
                                                              : isMetricCol
                                                              ? "#444B51"
                                                              : "#333333",
                                                      }}
                                                  >
                                                      {display}
                                                  </Typography>
                                              </Box>
                                          );
                                      })}
                                  </Box>
                              );
                          })}
                </Box>
            </Box>
        </Box>
    );
}
